import styled from '@emotion/styled';

import {
  ColorGray,
  ColorGreen,
  ColorPrimary,
  ColorWhite,
} from '../../../shared/GlobalStyles';

// Shared `Text` styledTextProps for the options-dropdown items in JournalEntry
// and Comment — one edit here updates both instead of two.
export const STANDALONE_DROPDOWN_TEXT_PROPS = {
  fontSize: 14,
  marginLeft: '10px',
  paddingTop: '2px',
  fontWeight: 900,
  textTransform: 'uppercase',
  letterSpacing: 1.3,
  color: ColorGray,
};

/** Factory rather than a constant: the Build variant renders inside Build's
 * ThemeProvider, so its label color has to follow the theme. Light resolves to
 * #444444 (the original value), leaving the standalone /journal page untouched. */
export const buildDropdownTextProps = (colors) => ({
  fontSize: 16,
  fontWeight: 400,
  color: colors.text,
});

// The Build-variant `.dropdown-menu`/`.dropdown-item` look, shared by
// JournalEntry's and Comment's OptionsDropdownWrapper below and by
// AttachmentButton's own dropdown wrapper (which has other unique CSS of its
// own around this — only the menu/item chrome itself is identical).
export const buildDropdownMenuCss = (theme) => {
  const c = theme?.colors || {};
  return `
  .dropdown-menu {
    /* Bootstrap paints .dropdown-menu white by default, so the background has
       to be set explicitly or the menu stays a white slab in dark mode. */
    background: ${c.surfaceElevated || 'white'};
    color: ${c.text};
    border: 1px solid ${c.borderLight || '#EEEEEE'};
    border-radius: 4px;
    box-shadow: 0px 4px 8px 0px #3031331A;
    padding: 8px 0px;
  }
  .dropdown-item {
    align-items: center;
    display: flex;
    gap: 8px;
    justify-content: flex-start;
    padding: 7px 16px;
    transition: none;
    color: ${c.text};
    :hover {
      background: ${c.surfaceSecondary || '#F5F5F5'};
      color: ${c.text};
    }
  }
`;
};

// The "⋮ options" dropdown wrapper shared verbatim by JournalEntry and
// Comment (edit/delete/report menus) — identical in both standalone and
// Build variants.
export const OptionsDropdownWrapper = styled('div')`
  .btn-primary {
    background-color: transparent;
    border: none;
    height: 20px;
    padding: 0;
    &hover {
      background-color: transparent;
      border: none;
    }
  }
  .cancel-edit {
    align-items: top;
    cursor: pointer;
    display: flex;
    height: 32px;
    text-align: center;
  }
  .show > .btn-primary.dropdown-toggle {
    background-color: transparent;
    border: none;
    color: ${ColorPrimary};
  }
  ${(props) => (props.isBuildVariant ? `
    /* Bootstrap draws a caret on .dropdown-toggle from currentColor, and the
       toggle inherits .btn-primary's white — so it was invisible against the
       white card and only appeared once the card went dark. The ⋮ icon is the
       real affordance; this caret was never meant to be seen. Scoped to the
       build variant so the standalone /journal look is untouched. */
    .dropdown-toggle::after {
      display: none;
    }
    ${buildDropdownMenuCss(props.theme)}
  ` : `
    .dropdown-menu {
      padding: 0px;
    }
    .dropdown-item {
      display: flex;
      justify-content: flex-start;
      align-items: center;
      padding: 5px;
      padding-left: 10px;
      transition: none;
      text-transform: uppercase;
      svg {
        fill: ${ColorGray};
      }
      :hover {
        transition: linear 0s;
        background: ${ColorGreen};
        color: ${ColorWhite};
        div p {
          color: ${ColorWhite};
        }
        svg {
          fill: ${ColorWhite};
        }
      }
    }
  `)}
`;

// The "⋮" dots icon itself — shared by JournalEntry and Comment. Both target
// the same `.journalOptionsDots` icon class for the hover-color rule.
// `buildPadding` covers the one real difference between the two call sites
// (JournalEntry renders it with no padding; Comment keeps the original 7px).
export const OptionsIconContainer = styled('div')`
  align-items: ${(props) => (props.isBuildVariant ? 'center' : 'flex-start')};
  cursor: pointer;
  display: flex;
  justify-content: center;
  ${(props) => (props.isBuildVariant ? `
    border-radius: 4px;
    padding: ${props.buildPadding ?? '7px'};
    transition: background 150ms ease-in-out;
    .journalOptionsDots {
      fill: rgba(189, 189, 189, 1);
    }
    :hover {
      background: rgba(119, 167, 255, 0.2);
    }
    &:hover .journalOptionsDots {
      fill: #77A7FF;
    }
  ` : `
    height: 32px;
    width: 32px;
  `)}
`;
